import { useNavigate, useParams } from 'react-router-dom';

import { cn } from 'lib/helpers/utility';
import useModal from 'lib/hooks/useModal';
import { isKioskDevice } from 'lib/helpers/kiosk';
import { useReceipt } from 'lib/hooks/queries/billing/useReceipt';
import { useSendReceipt } from 'lib/hooks/queries/billing/useSendReceipt';
import { useReceiptDelivery } from 'lib/hooks/queries/billing/useReceiptDelivery';
import { RECEIPT_CHANNELS, isChannelUsed, isChannelAvailable } from 'lib/helpers/receiptDelivery';

import Icon from 'components/shared/Icon/Icon';
import Button from 'components/shared/Button/Button';
import Typography from 'components/shared/Typography/Typography';
import ReceiptModal from 'components/shared/Receipt/ReceiptModal';

const SuccessPayment = ({ transactionId, isKiosk, token: tokenProp }) => {
  const { isOpen, openModal, closeModal } = useModal();
  const navigate = useNavigate();
  const params = useParams();
  const token = tokenProp ?? params.token;
  const kiosk = isKioskDevice();

  // The patient sends each channel once; a send by staff or the automatic kiosk receipt
  // arrives over the kiosk socket and disables the channel here too.
  const delivery = useReceiptDelivery(transactionId, { listenToKiosk: true });
  const isEmailed = isChannelUsed(delivery[RECEIPT_CHANNELS.EMAIL]);
  const isTexted = isChannelUsed(delivery[RECEIPT_CHANNELS.SMS]);

  // Same key as ReceiptModal's query, so opening the receipt reuses this response.
  const { data: receiptData, isFetched: isReceiptFetched } = useReceipt({
    params: { transactionId, token },
    dependencies: [transactionId, undefined, undefined, token],
    options: { enabled: !!transactionId }
  });
  const channels = receiptData?.deliveryChannels;
  const canEmail = isReceiptFetched && isChannelAvailable(channels, RECEIPT_CHANNELS.EMAIL);
  const canText = isReceiptFetched && isChannelAvailable(channels, RECEIPT_CHANNELS.SMS);

  const receiptParams = { transactionId, token };
  const { mutate: emailReceipt, isLoading: isEmailing } = useSendReceipt({
    medium: RECEIPT_CHANNELS.EMAIL,
    params: receiptParams
  });
  const { mutate: textReceipt, isLoading: isTexting } = useSendReceipt({
    medium: RECEIPT_CHANNELS.SMS,
    params: receiptParams
  });

  const receiptButtonProps = {
    variant: kiosk ? 'secondary' : 'tertiary',
    size: kiosk ? 'large' : 'small',
    color: kiosk ? 'primary' : 'neutral',
    iconColor: kiosk ? 'primary' : undefined,
    iconSize: kiosk ? 26 : undefined,
    textClassName: cn('whitespace-nowrap', kiosk && 'text-xl'),
    wrapperClassName: 'w-full',
    className: cn('w-full', kiosk && 'h-16 rounded-xl')
  };

  return (
    <div className="fixed inset-0 h-[var(--app-height,100vh)] w-screen overflow-hidden p-10 sm:p-4">
      <Icon
        icon="bg-pattern"
        className="absolute inset-0 h-full w-full [&>div]:absolute [&>div]:inset-0 [&>div]:h-full [&>div]:w-full"
      />
      <div
        className={cn(
          'relative flex h-full w-full flex-col items-center justify-center',
          kiosk ? 'gap-6' : 'gap-3'
        )}>
        <div className="flex flex-col items-center justify-center">
          <Icon icon="illustration-payment-successful" size={kiosk ? 220 : 160} />
          <Typography
            variant="p"
            size="medium"
            weight={kiosk ? 'semibold' : 'medium'}
            className={kiosk ? '!text-3xl' : undefined}>
            Payment successful
          </Typography>
          <Typography
            variant="p"
            size="small"
            color="neutral-700"
            className={kiosk ? '!text-lg' : undefined}>
            The payment was processed successfully.
          </Typography>
        </div>
        <div
          className={cn(
            'flex flex-col items-center justify-center',
            kiosk ? 'w-full max-w-[600px] gap-4' : 'min-w-[280px] gap-2'
          )}>
          <div
            className={cn(
              'flex w-full items-center sm:flex-col',
              kiosk ? 'mt-4 gap-4' : 'mt-3 gap-2'
            )}>
            <Button
              {...receiptButtonProps}
              text="View receipt"
              icon="receipt-1-mono"
              onClick={openModal}
            />
            {canEmail && (
              <Button
                {...receiptButtonProps}
                text={isEmailed ? 'Receipt emailed' : 'Email receipt'}
                icon={isEmailed ? 'tick-circle-mono' : 'mail-mono'}
                onClick={() => emailReceipt()}
                isLoading={isEmailing}
                isDisabled={isEmailed}
              />
            )}
            {canText && (
              <Button
                {...receiptButtonProps}
                text={isTexted ? 'Receipt texted' : 'Text receipt'}
                icon={isTexted ? 'tick-circle-mono' : 'message-mono'}
                onClick={() => textReceipt()}
                isLoading={isTexting}
                isDisabled={isTexted}
              />
            )}
          </div>
          <Button
            text="Close"
            variant={kiosk ? 'primary' : 'tertiary'}
            size={kiosk ? 'large' : 'small'}
            textClassName={kiosk ? 'text-xl' : undefined}
            wrapperClassName="w-full"
            className={cn(
              'w-full',
              kiosk &&
                'h-16 rounded-xl shadow-[0_10px_24px_-8px_color-mix(in_srgb,var(--color-primary-600,#0B85C9)_50%,transparent)] transition-transform active:scale-[0.98]'
            )}
            onClick={() => navigate(isKiosk ? '/kiosk/idle' : '/')}
          />
        </div>
      </div>
      <ReceiptModal
        transactionId={transactionId}
        isOpen={isOpen}
        onClose={closeModal}
        token={token}
      />
    </div>
  );
};

export default SuccessPayment;
